import * as Parser from "papaparse";
import {get} from "lodash";
import { thor, SalePublishRes } from "../../../typings/thor";
import { ImportCsvBaseService } from "./import-csv-base.service";

export class ImportSaleResultService extends ImportCsvBaseService {
    public static $inject: string[] = ["$log", "$q", "$translate", "itemService", "saleService"];

    constructor(
        private $log: ng.ILogService,
        protected $q: ng.IQService,
        $translate: ng.translate.ITranslateService,
        protected itemService,
        private saleService
    ) {
        super($q, $translate, itemService);
    }

    public engine(sale, file) {
        this.sale = sale;
        this.globalDeferred = this.$q.defer();
        this.summary = {
            countUnsold: 0,
            countSold: 0,
            countNC: 0,
            updated: 0,
            errors: []
        };

        const parseConfig: Parser.ParseConfig = {
            skipEmptyLines: false,
            dynamicTyping: true,
            delimiter: ((input) => this.guessDelimiter(input).bestColumnDelimiter) as unknown as string,
            encoding: "",
            complete: results => {
                if (results && results.data) {
                    this.engineRows(results.data)
                        .then(() => {
                            this.summary.updatedPercent =
                                Math.ceil(100 * this.summary.updated / this.sale.items.length);
                            return this.sendSummary();
                        })
                        .then(() => {
                            this.globalDeferred.resolve();
                        })
                        .catch(error => {
                            this.globalDeferred.reject(error);
                        });
                } else {
                    this.globalDeferred.reject();
                }
            },
            error: error => {
                this.globalDeferred.reject(error);
            }
        };

        Parser.parse(file, parseConfig);

        return this.globalDeferred.promise;
    }

    private engineRows(rows) {
        this.notify("setGauge", { gauge: "rows", counter: "total", value: rows.length });

        const promises = [];
        let line = 1;

        rows.forEach(row => {
            const formattedRow: any = {
                line: line++
            };

            if (row.length === 3) {
                formattedRow.orderNumberPrimary = row[0];
                formattedRow.orderNumberSecondary = row[1];
                formattedRow.price = this.cleanPrice(row[2]);
            }

            if (row.length === 2) {
                const firstCol = row[0];
                formattedRow.price = this.cleanPrice(row[1]);
                if (firstCol === parseInt(firstCol, 10)) {
                    formattedRow.orderNumberPrimary = firstCol;
                    formattedRow.orderNumberSecondary = "";
                } else {
                    const pattern = new RegExp("^([0-9]+)[\\_\\-\\s]?([A-Za-z]*)$", "i");
                    const match = pattern.exec(firstCol);
                    if (match) {
                        formattedRow.orderNumberPrimary = parseInt(match[1], 10);
                        formattedRow.orderNumberSecondary = match[2];
                    }
                }
            }

            promises.push(
                this.engineRow(formattedRow, rows)
            );
        });

        return this.$q.all(promises).then(() =>
            this.saleService
                .publish(this.sale.id)
                .then((res: SalePublishRes) => this.$log.info(`Sale ${this.sale.id} published with success`, res))
                .catch(error => {
                    this.$log.error(`Error while publishing the sale ${this.sale.id}`, error);
                    return this.$q.reject(error);
                })
        );
    }

    private engineRow(row, rows) {
        if (!this.isCorrectRow(row)) {
            this.notify("incrementGauge", { gauge: "rows",  counter: "error" });
            return this.$q.resolve();
        }

        const matchedItems = this.findMatchedItems(row);

        if (this._checkDuplicateRow(row, rows)) {
            this.summary.errors.push({
                row: row,
                message: this.$t("doublon_detecte_fichier_csv")
            });

            this.notify("incrementGauge", { gauge: "rows", counter: "error" });
            return this.$q.resolve();
        }

        if (matchedItems.length > 0) {
            if (matchedItems.length > 1) {
                this.summary.errors.push({
                    row: row,
                    message: this.$t("doublon_detecte_numero_d_ordre")
                });

                this.notify("incrementGauge", { gauge: "rows", counter: "error" });
                return this.$q.resolve();
            }
            return this.updateItem(matchedItems[0], row)
                .then(() => {
                    this.notify("incrementGauge", { gauge: "rows", counter: "valid" });
                })
                .catch(() => {
                    this.summary.errors.push({
                        row: row,
                        message: this.$t("erreur_a_l_enregistrement")
                    });
                    this.notify("incrementGauge", { gauge: "rows", counter: "error" });
                });
        } else {
            this.summary.errors.push({
                row: row,
                message: this.$t("lot_introuvable")
            });
            this.notify("incrementGauge", { gauge: "rows", counter: "error" });
            return this.$q.resolve();
        }
    }

    private cleanPrice(price) {
        if (typeof price !== "string") {
            return price;
        }
        // @todo internationalization needed?
        return price.replace(/euros?|€|\s/gi, "");
    }

    private isCorrectRow(row) {
        if (
            row.orderNumberPrimary === "" &&
            !row.orderNumberSecondary &&
            !row.price
        ) {
            this.summary.errors.push({
                row: row,
                message: this.$t("ligne_vide")
            });
            return false;
        }

        // orderNumberPrimary must be a positive integer
        if (
            row.orderNumberPrimary === 0 ||
            row.orderNumberPrimary !== parseInt(row.orderNumberPrimary, 10)
        ) {
            this.summary.errors.push({
                row: row,
                message: this.$t("numero_d_ordre_invalide")
            });
            return false;
        }

        // Case with declared price but not with an integer
        if (row.price) {
            // Handle ,.00
            row.price = typeof row.price === "string" ? row.price.replace(/[,.]00/, "") : row.price;

            if ( /[.,]+/.test(row.price) ||
                isNaN(parseInt(row.price, 10))
            ) {
                this.summary.errors.push({
                    row: row,
                    message: `${this.$t("prix_d_adjudication_invalide")} : ${ row.price }`
                });
                return false;
            }
        }

        return true;
    }

    private findMatchedItems(row) {
        const orderNumberPrimary = row.orderNumberPrimary;
        const orderNumberSecondary = row.orderNumberSecondary.toString();

        return this.sale.items.filter(item => {
            const itemOrderNumberPrimary = get(item, "meta.order_number.primary", 0) || 0;
            const itemOrderNumberSecondary = get(item, "meta.order_number.secondary", "") || "";

            if (
                itemOrderNumberPrimary === orderNumberPrimary &&
                itemOrderNumberSecondary.toLowerCase() === orderNumberSecondary.toLowerCase()
            ) {
                return true;
            }

            return false;
        });
    }

    private updateItem (item, row) {
        const itemAuctioned = get(item, "pricing.auctioned", null);
        const parsedPrice = parseInt(row.price, 10);
        let auctioned: thor.ItemPricingAuctioned;

        if (parsedPrice === -1) {
            this.summary.countUnsold++;
            auctioned = {
                sold: false,
                price: null
            };
        } else if (!isNaN(parsedPrice) && parsedPrice > 0) {
            this.summary.countSold++;
            auctioned = {
                sold: true,
                price: parsedPrice
            };
        } else {
            this.summary.countNC++;
            auctioned = null;
        }

        if (angular.equals(itemAuctioned, auctioned)) {
            this.summary.updated++;
            return this.$q.resolve();
        }

        const data: thor.ItemPost = {
            pricing: {
                auctioned: auctioned
            }
        };

        this.summary.updated++;
        return this.itemService.update(item.id, data);
    }

    private _checkDuplicateRow(formattedRow, rows) {
        return !!rows.find((row, index) => {
            if (index !== formattedRow.line - 1) {
                if (row.length === 2) {
                    return row[0] === formattedRow.orderNumberPrimary;
                }
                if (row.length === 3) {
                    return row[0] === formattedRow.orderNumberPrimary &&
                        row[1] === formattedRow.orderNumberSecondary;
                }
            }
        });
    }
}
